<template>
  <div class="admin-dashboard admin-dashboard-modern forms-page">
    <CommonOverlay :show="isLoadingConversations" title="Loading Dashboard Data..." />
    <div class="page-header">
      <h2 class="page-title">Your practice's AI activity</h2>
    </div>

    <!-- Three REAL stat tiles — all numbers are DB-backed (teamio-bot
         lib/dashboardStats.js). Each tile shows an honest state: a spinner while
         loading, the real number on success (0 included), a "Not available" note
         on a 403, or an error note otherwise. No fabricated numbers. -->
    <div class="stats-overview-container">
      <!-- Teamio Agent -->
      <div class="stat-card-modern">
        <div class="stat-card-modern-header">
          <div class="stat-icon-wrapper text-bot-icon-bg">
            <i class="fa fa-magic section-icon"></i>
          </div>
          <h5 class="stat-title">Teamio Agent</h5>
        </div>
        <div class="stat-card-modern-body">
          <div v-if="dashboardLoading.agent" class="tile-state tile-state-loading">
            <i class="fa fa-spinner fa-spin"></i> Loading…
          </div>
          <div v-else-if="dashboardAccess.agent === 'not-available'" class="tile-state tile-state-muted">
            Not available
          </div>
          <div v-else-if="dashboardAccess.agent === 'error'" class="tile-state tile-state-error">
            Couldn't load
          </div>
          <div v-else class="tile-headline-block">
            <div class="tile-headline">
              <span class="tile-headline-number">{{ fmt(dashboardStats.agent.range) }}</span>
              <span class="tile-headline-unit">questions asked</span>
            </div>
            <div class="tile-subline">{{ rangeSubline }}</div>
            <div class="tile-subtext">Your in-app AI assistant.</div>
          </div>
        </div>
        <div class="stat-card-modern-footer">
          <router-link :to="{ name: 'CalendarAdminBotConversationList' }" class="view-details-link">
            View Details <i class="fa fa-arrow-right"></i>
          </router-link>
        </div>
      </div>

      <!-- AI Receptionist (inbound calls + texts) -->
      <div class="stat-card-modern">
        <div class="stat-card-modern-header">
          <div class="stat-icon-wrapper voice-bot-icon-bg">
            <i class="fa fa-headphones section-icon"></i>
          </div>
          <h5 class="stat-title">AI Receptionist</h5>
        </div>
        <div class="stat-card-modern-body">
          <div v-if="dashboardLoading.receptionist" class="tile-state tile-state-loading">
            <i class="fa fa-spinner fa-spin"></i> Loading…
          </div>
          <div v-else-if="dashboardAccess.receptionist === 'not-available'" class="tile-state tile-state-muted">
            Not available
          </div>
          <div v-else-if="dashboardAccess.receptionist === 'error'" class="tile-state tile-state-error">
            Couldn't load
          </div>
          <div v-else class="tile-headline-block">
            <div class="tile-headline">
              <span class="tile-headline-number">{{ fmt(dashboardStats.receptionist.callsRange) }}</span>
              <span class="tile-headline-unit">calls handled</span>
            </div>
            <div class="tile-subline">{{ rangeSubline }}</div>
            <div v-if="receptionistTextsLine" class="tile-subline tile-subline-secondary">{{ receptionistTextsLine }}</div>
            <div class="tile-subtext">Inbound patient calls.</div>
          </div>
        </div>
        <div class="stat-card-modern-footer">
          <router-link :to="{ name: 'CalendarAdminBotConversationList' }" class="view-details-link">
            View Details <i class="fa fa-arrow-right"></i>
          </router-link>
        </div>
      </div>

      <!-- Appointments Booked (via Teamio) -->
      <div class="stat-card-modern">
        <div class="stat-card-modern-header">
          <div class="stat-icon-wrapper appointment-icon-bg">
            <i class="fa fa-calendar-check-o section-icon"></i>
          </div>
          <h5 class="stat-title">Appointments Booked</h5>
        </div>
        <div class="stat-card-modern-body">
          <div v-if="dashboardLoading.appointments" class="tile-state tile-state-loading">
            <i class="fa fa-spinner fa-spin"></i> Loading…
          </div>
          <div v-else-if="dashboardAccess.appointments === 'not-available'" class="tile-state tile-state-muted">
            Not available
          </div>
          <div v-else-if="dashboardAccess.appointments === 'error'" class="tile-state tile-state-error">
            Couldn't load
          </div>
          <div v-else class="tile-headline-block">
            <div class="tile-headline">
              <span class="tile-headline-number">{{ fmt(dashboardStats.appointments.range) }}</span>
              <span class="tile-headline-unit">booked</span>
            </div>
            <div class="tile-subline">{{ rangeSubline }}</div>
            <div class="tile-subtext">Booked via Teamio.</div>
          </div>
        </div>
        <div class="stat-card-modern-footer">
          <router-link :to="{ name: 'CalendarAdminAppointmentStats' }" class="view-details-link">
            View Details <i class="fa fa-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Recent Activity Section - Styled like FormsHome lists -->
    <div class="task-details-item recent-activity-panel">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-history section-icon"></i>
          Recent Voice Calls
        </h5>
      </div>

      <div v-if="isLoadingConversations && recentVoiceCalls.length === 0" class="empty-state empty-state-modern">
        <p>Loading recent calls...</p>
      </div>
      <div v-else-if="!isLoadingConversations && recentVoiceCalls.length === 0" class="empty-state empty-state-modern">
        <i class="fa fa-folder-open-o empty-state-icon"></i>
        <p>No recent voice calls to display.</p>
      </div>

      <div v-else class="activity-list-modern">
        <div v-for="activity in recentVoiceCalls" :key="activity.id" class="activity-item-modern">
          <div class="activity-icon-modern" :class="getIconClass(activity.type)">
            <i :class="getFaIcon(activity.type)"></i>
          </div>
          <div class="activity-content-modern">
            <div class="activity-title-modern">{{ activity.title }}</div>
            <div class="activity-description-modern">{{ activity.description }}</div>
          </div>
          <div class="activity-time-modern">{{ activity.time }}</div>
          <div class="activity-action-modern">
            <router-link :to="activity.link" class="action-link-btn">
              View <i class="fa fa-angle-right"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import CommonOverlay from '@/components/CommonOverlay.vue';
import mixin from '../../../mixin.js';
import { formatStatNumber, formatTextsLine, rangeLabel } from '@/utils/dashboardStats';

export default {
  name: 'AdminDashboard',
  mixins: [mixin],
  components: {
    CommonOverlay
  },
  computed: {
    ...mapState('botAdmin', {
      rawConversations: state => state.conversations,
      isLoadingConversations: state => state.isLoading,
    }),

    // The three REAL dashboard tiles (DB-backed via botAdmin store) + the shared
    // date-range selection (single source of truth for the picker + the tiles).
    ...mapGetters('botAdmin', {
      dashboardStats: 'dashboardStats',
      dashboardLoading: 'dashboardLoading',
      dashboardAccess: 'dashboardAccess',
      dashboardRange: 'dashboardRange',
    }),

    // The human label for the selected range (e.g. "All time" / "Last 7 days"). This
    // REPLACES the old "today" subline — the headline is now the RANGE count, so the
    // subline states the period instead of a contradicting "None today".
    rangeSubline() {
      return rangeLabel(this.dashboardRange);
    },

    // "+ N texts" secondary line for the AI Receptionist tile — scoped to the selected
    // range (textsRange), only shown when > 0.
    receptionistTextsLine() {
      return formatTextsLine(this.dashboardStats.receptionist.textsRange);
    },

    elevenLabsAgentId() {
      if (!this.allSettings) return null;
      return this.allSettings.ELEVEN_LABS_AGENT_ID ? this.allSettings.ELEVEN_LABS_AGENT_ID.PropertyValue : null;
    },

    voiceConversations() {
      if (!this.rawConversations || !this.rawConversations.items) {
        return [];
      }
      return this.rawConversations.items.filter(conv => conv.type === 'voice');
    },

    recentVoiceCalls() {
      return this.voiceConversations
        .slice(0, 5)
        .map(conv => ({
          id: conv.conversation_id,
          type: 'voice',
          title: `Voice Call (${conv.status || 'N/A'})`,
          description: `Call from: ${conv.from_number || 'Unknown'} to ${conv.to_number || 'Unknown'}`,
          time: conv.created_at_unix ? moment.unix(conv.created_at_unix).fromNow() : 'Unknown time',
          link: { name: 'CalendarAdminBotConversationDetail', params: { id: conv.conversation_id } }
        }));
    }
  },
  async mounted() {
    if (this.elevenLabsAgentId) {
      await this.fetchData();
    } else {
      // console.warn('AdminDashboard: ElevenLabs Agent ID not available on initial mount. Waiting for allSettings from mixin...');
    }
    // The three real tiles only need the siteID — fetch them independently of the
    // ElevenLabs settings (the siteID watcher re-fetches if it hydrates later).
    if (this.siteID) {
      this.fetchTileStats();
    }
  },
  methods: {
    ...mapActions('botAdmin', [
      'fetchElevenLabsConversations',
      'fetchAgentStats',
      'fetchReceptionistStats',
      'fetchAppointmentsStats',
    ]),

    // Number formatter for the tiles (pure helper — see utils/dashboardStats).
    fmt(n) {
      return formatStatNumber(n);
    },

    // Fire the three real stat endpoints in parallel. Each action isolates its own
    // loading/access state, so a 403/error on one tile never blanks the others.
    fetchTileStats() {
      if (!this.siteID) return;
      const args = { siteId: this.siteID };
      this.fetchAgentStats(args);
      this.fetchReceptionistStats(args);
      this.fetchAppointmentsStats(args);
    },

    async fetchData() {
      if (!this.elevenLabsAgentId) {
        // console.error('Cannot fetch conversations: ElevenLabs Agent ID is missing.');
        return;
      }
      try {
        await this.fetchElevenLabsConversations({
          agentId: this.elevenLabsAgentId,
          pageSize: 50,
        });
      } catch (error) {
        // console.error('Error fetching dashboard conversations:', error);
      }
    },
    getIconClass(type) {
      if (type === 'text') return 'text-bot-icon-bg';
      if (type === 'voice') return 'voice-bot-icon-bg';
      if (type === 'appointment') return 'appointment-icon-bg';
      return 'default-icon-bg';
    },
    getFaIcon(type) {
      if (type === 'text') return 'fa fa-commenting-o';
      if (type === 'voice') return 'fa fa-phone';
      if (type === 'appointment') return 'fa fa-calendar-plus-o';
      return 'fa fa-info-circle';
    }
  },
  watch: {
    allSettings: {
      handler(newSettings) {
        if (newSettings && this.elevenLabsAgentId) {
          // console.log('AdminDashboard: allSettings updated via mixin, Agent ID found, fetching/re-fetching dashboard data...');
          this.fetchData();
        } else if (newSettings && !this.elevenLabsAgentId){
          // console.warn('AdminDashboard: allSettings updated via mixin, but ELEVEN_LABS_AGENT_ID still not found.');
        }
      },
    },
    // The siteID may hydrate after mount (customer store loads async). When it
    // first becomes available, fetch the three real tiles.
    siteID(newSiteId, oldSiteId) {
      if (newSiteId && newSiteId !== oldSiteId) {
        this.fetchTileStats();
      }
    },
    // Re-fetch all three tiles whenever the shared date-range changes (the picker in
    // AdminLayout commits it). The endpoint recomputes the range count for the new
    // window; honest loading/0/not-available/error states still apply per tile.
    dashboardRange() {
      this.fetchTileStats();
    },
  }
};
</script>

<style lang="scss" scoped>
// Import modern design system from AppointmentCalendarModern.scss
@import '../AppointmentCalendarModern.scss';

// Treatment Presenter design tokens (inherited from .admin-layout via the cascade).
// SCSS vars below now resolve to the --aid-* custom properties so the whole tile/
// activity surface tracks the shared palette instead of the old bright green/blue.
$primary-color: var(--aid-accent);
$text-color: var(--aid-text);
$text-light: var(--aid-text-mute);
$text-dim: var(--aid-text-dim);
$border-color: var(--aid-border);
$background-light: var(--aid-bg);
$surface2: var(--aid-surface2);
$white: var(--aid-surface);
$card-shadow: var(--aid-shadow-card);
$border-radius: 10px;

// Mixin for flex centering (example)
@mixin flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.forms-page { // Applied to the root for consistency with FormsHome
  padding: 1.5rem;
  background-color: transparent; // sits on the .admin-layout --aid-bg surface
  min-height: 100vh;
  font-family: var(--aid-font);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $border-color;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: $text-color;
    letter-spacing: -0.1px;
  }
}

// New Stats Cards Styling (inspired by FormsOverview and general modern card design)
// Desktop: the three tiles sit in a clean equal-width row (max 3 columns so they
// never stretch awkwardly wide on an ultra-wide screen). `auto-fit` (not auto-fill)
// collapses empty tracks so 3 tiles fill the row with no phantom gap. Tablet drops
// to 2-up and mobile to 1-up via the media queries below.
.stats-overview-container {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

// Tablet — 2-up so tiles keep a comfortable width beside the rail.
@media (max-width: 991px) {
  .stats-overview-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
}

.stat-card-modern {
  background-color: $white;
  border-radius: $border-radius;
  box-shadow: $card-shadow;
  border: 1px solid $border-color;
  display: flex;
  flex-direction: column;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--aid-shadow-md), 0 10px 22px rgba(15, 23, 42, 0.07);
    border-color: var(--aid-border-strong);
  }
}

.stat-card-modern-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid $border-color;

  // Cohesive accent treatment: a teal glyph on a soft rounded square (10px),
  // replacing the bright primary-color circles. A subtle per-tile tint keeps the
  // three tiles distinguishable but stays inside the restrained teal/slate family.
  .stat-icon-wrapper {
    @include flex-center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    margin-right: 0.75rem;
    flex-shrink: 0;
    background-color: var(--aid-accent-soft);
    color: var(--aid-accent);

    .section-icon {
      font-size: 1.05rem;
      color: var(--aid-accent);
    }
  }
  // Per-tile tint, restrained palette: agent = teal, receptionist = success teal,
  // appointments = a calm slate. Each keeps a soft bg + matching glyph.
  .text-bot-icon-bg { background-color: var(--aid-accent-soft); color: var(--aid-accent); .section-icon { color: var(--aid-accent); } }
  .voice-bot-icon-bg { background-color: #e3f3f3; color: var(--aid-success); .section-icon { color: var(--aid-success); } }
  .appointment-icon-bg { background-color: var(--aid-surface2); color: var(--aid-text-mute); .section-icon { color: var(--aid-text-mute); } }
  .default-icon-bg { background-color: var(--aid-surface2); color: var(--aid-text-dim); .section-icon { color: var(--aid-text-dim); } }


  .stat-title {
    font-size: 14px;
    font-weight: 600;
    color: $text-color;
    margin: 0;
  }
}

.stat-card-modern-body {
  padding: 1.25rem;
  flex-grow: 1;
}

.stat-item-modern {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  .stat-label-modern {
    font-size: 0.875rem;
    color: $text-light;
  }

  .stat-value-modern {
    font-size: 1.2rem;
    font-weight: 600;
    color: $text-color;
  }
}

// --- Real-tile headline + honest states ---
.tile-headline-block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tile-headline {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.4rem;

  .tile-headline-number {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.1;
    color: $text-color;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.5px;
  }

  .tile-headline-unit {
    font-size: 0.9rem;
    color: $text-light;
  }
}

.tile-subline {
  font-size: 13px;
  color: $text-light;

  &.tile-subline-secondary {
    color: $text-dim;
  }
}

.tile-subtext {
  font-size: 12px;
  color: $text-dim;
  margin-top: 0.25rem;
}

.tile-state {
  font-size: 0.95rem;
  padding: 0.5rem 0;

  &.tile-state-loading { color: $text-dim; }
  &.tile-state-muted { color: $text-dim; font-weight: 500; }
  &.tile-state-error { color: var(--aid-danger-text); }
}

.stat-card-modern-footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid $border-color;
  text-align: right;

  .view-details-link {
    color: $primary-color;
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    transition: color 0.15s ease;

    i {
      margin-left: 0.25rem;
      transition: transform 0.15s ease;
    }

    &:hover {
      color: var(--aid-accent-ink);
      i {
        transform: translateX(3px);
      }
    }
  }
}


// Recent Activity - Styled like FormsHome lists (task-details-item)
.task-details-item { // Re-using class from FormsHome for consistency
  background-color: $white;
  border-radius: $border-radius;
  box-shadow: $card-shadow;
  margin-bottom: 1.5rem; // Space between sections
  border: 1px solid $border-color; // Consistent border
  overflow: hidden; // Prevent content spill

  &.recent-activity-panel { // Specific class for this panel
    // any specific overrides if needed
  }
}

.task-details-item-header { // Consistent header style
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid $border-color;
  background-color: var(--aid-surface2); // soft header strip

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: $text-color;
    margin: 0;
    display: flex;
    align-items: center;

    .section-icon {
      margin-right: 0.6rem;
      color: $primary-color; // Icon color consistent with theme
      font-size: 1rem;
    }
  }
}

.empty-state-modern { // New styling for empty state
  padding: 2rem 1.25rem;
  text-align: center;
  color: $text-dim;

  .empty-state-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    color: var(--aid-border-strong);
  }
  p {
    font-size: 13px;
  }
}

.activity-list-modern {
  // No specific container style needed if items handle their own borders
}

.activity-item-modern {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid $border-color;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--aid-surface2); // subtle neutral hover
  }

  // Voice-call glyph: teal on soft-teal rounded square, in line with the tiles.
  .activity-icon-modern {
    @include flex-center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    margin-right: 1rem;
    flex-shrink: 0;
    background-color: var(--aid-accent-soft);

    i {
      font-size: 1rem;
      color: var(--aid-accent);
    }

    // Per-type tint overrides (still in the restrained palette).
    &.voice-bot-icon-bg { background-color: #e3f3f3; i { color: var(--aid-success); } }
    &.text-bot-icon-bg { background-color: var(--aid-accent-soft); i { color: var(--aid-accent); } }
    &.appointment-icon-bg { background-color: var(--aid-surface2); i { color: var(--aid-text-mute); } }
    &.default-icon-bg { background-color: var(--aid-surface2); i { color: var(--aid-text-dim); } }
  }

  .activity-content-modern {
    flex-grow: 1;
    margin-right: 1rem;

    .activity-title-modern {
      font-weight: 600;
      color: $text-color;
      font-size: 13px;
      margin-bottom: 0.15rem;
    }

    .activity-description-modern {
      font-size: 12px;
      color: $text-light;
      line-height: 1.4;
    }
  }

  .activity-time-modern {
    font-size: 12px;
    color: $text-dim;
    white-space: nowrap;
    margin-right: 1rem;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }

  .activity-action-modern {
    flex-shrink: 0;
    // Secondary button token style: white bg, text ink, soft border, surface2 hover.
    .action-link-btn {
      display: inline-flex;
      align-items: center;
      padding: 0.35rem 0.75rem;
      font-size: 12px;
      font-weight: 500;
      color: $text-color;
      background-color: var(--aid-surface);
      border: 1px solid $border-color;
      border-radius: 8px;
      text-decoration: none;
      transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

      i {
        margin-left: 0.3rem;
        font-size: 0.7rem;
      }

      &:hover {
        background-color: var(--aid-surface2);
        border-color: var(--aid-border-strong);
        color: var(--aid-accent-ink);
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    .page-title {
      margin-bottom: 0.5rem;
    }
  }
  .stats-overview-container {
    grid-template-columns: 1fr; // Stack cards on smaller screens
  }

  .activity-item-modern {
    flex-wrap: wrap; // Allow items to wrap on small screens

    .activity-content-modern {
      margin-bottom: 0.5rem; // Add space if time/action wraps below
      width: calc(100% - 52px); // Adjust width considering icon
    }
    .activity-time-modern {
       width: 100%;
       text-align: left;
       margin-top: 0.25rem;
       margin-bottom: 0.5rem;
       padding-left: 52px; // Align with content above icon
    }
    .activity-action-modern {
      width: 100%;
      text-align: right;
      padding-left: 52px; // Align with content
    }
  }
}

@media (max-width: 480px) {
  .forms-page {
    padding: 1rem;
  }
  .page-header .page-title {
    font-size: 1.5rem;
  }
  .stat-card-modern-header {
    padding: 0.75rem 1rem;
    .stat-icon-wrapper {
      width: 36px;
      height: 36px;
      .section-icon { font-size: 1.1rem; }
    }
    .stat-title { font-size: 1rem; }
  }
  .stat-card-modern-body { padding: 1rem; }
  .stat-item-modern {
    .stat-label-modern { font-size: 0.8rem; }
    .stat-value-modern { font-size: 1.1rem; }
  }
   .activity-item-modern {
    padding: 0.75rem 1rem;
    .activity-icon-modern {
        margin-right: 0.75rem;
    }
     .activity-content-modern {
        width: calc(100% - 48px); // Adjust width considering icon
      }
      .activity-time-modern, .activity-action-modern {
          padding-left: 48px; // Align with content
      }
   }
}

</style> 